import React, { useState, useRef, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import { WebinarItem } from '../../../api/analytics/dto/WebinarsListResp.dto';
import styles from './WebinarAutocomplete.module.css';

interface WebinarAutocompleteProps {
  onSelect: (name: string) => void;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const WebinarAutocomplete: React.FC<WebinarAutocompleteProps> = ({
  onSelect,
  value,
  onChange,
  placeholder = 'Начните вводить название вебинара...',
}) => {
  const api = useApi();
  const [suggestions, setSuggestions] = useState<WebinarItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchWebinars = async (searchText: string) => {
    if (searchText.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.analytics.getWebinarsList({
        text: searchText,
        limit: 10,
      });

      if (response.success && response.body && response.body.webinars) {
        setSuggestions(response.body.webinars || []);
        setIsOpen(true);
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при поиске вебинаров:', error);
      setSuggestions([]);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    searchWebinars(newValue);
  };

  const handleSelect = (webinar: WebinarItem) => {
    onSelect(webinar.name);
    onChange('');
    setSuggestions([]);
    setIsOpen(false);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU');
    } catch {
      return dateString;
    }
  };

  return (
    <div className={styles.autocomplete}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={styles.input}
        autoComplete="off"
      />

      {loading && <div className={styles.loading}>Поиск...</div>}

      {isOpen && suggestions && suggestions.length > 0 && (
        <div ref={dropdownRef} className={styles.dropdown}>
          {suggestions.map((webinar, index) => (
            <div
              key={index}
              className={styles.suggestion}
              onClick={() => handleSelect(webinar)}
            >
              <div className={styles.webinarName}>{webinar.name}</div>
              <div className={styles.webinarInfo}>
                <span className={styles.count}>
                  {webinar.count} регистраций
                </span>
                <span className={styles.date}>
                  Последняя: {formatDate(webinar.last_reg_date)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {isOpen &&
        (!suggestions || suggestions.length === 0) &&
        !loading &&
        value.length >= 2 && (
          <div ref={dropdownRef} className={styles.dropdown}>
            <div className={styles.noResults}>Вебинары не найдены</div>
          </div>
        )}
    </div>
  );
};

export default WebinarAutocomplete;
